import { expectArray, expectBoolean, expectNullable, expectRecord, expectString } from "./schema";
import { STUDENT_BILLING_CHARGE_TYPES, parseStudentBillingAmount, parseStudentBillingDate, parseStudentBillingIdentifier, parseStudentBillingServiceMonth } from "./student-billing";
import { STUDENT_BILLING_PAYMENT_EXECUTION_STATUSES, STUDENT_BILLING_SAFE_PAYMENT_FAILURE_CODES } from "./student-billing-charge-execution";
import { STUDENT_BILLING_PAYMENT_SETUP_STATUSES } from "./student-billing-payment-setup";
import { parseSafePaymentCard } from "./tenant-application-payment-setup";
import { STUDENT_BILLING_COLLECTION_METHODS } from "./student-billing-collection-authority";
import { parseStudentBillingPayerCustomerInvoiceDto } from "./student-billing-customer-invoice-execution";
function exact(record, allowed, path) {
    const unknown = Object.keys(record).filter((key) => !allowed.includes(key));
    if (unknown.length > 0)
        throw new Error(`${path} contains unknown fields: ${unknown.sort().join(", ")}`);
}
function nullableString(value, path) {
    return expectNullable(value, (item, itemPath) => expectString(item, itemPath), path);
}
function isoString(value, path) {
    const parsed = expectString(value, path);
    if (Number.isNaN(new Date(parsed).getTime()))
        throw new Error(`${path} must be an ISO date-time`);
    return parsed;
}
function nullableIsoString(value, path) {
    const parsed = nullableString(value, path);
    if (parsed !== null && Number.isNaN(new Date(parsed).getTime()))
        throw new Error(`${path} must be an ISO date-time or null`);
    return parsed;
}
function parsePaymentMethod(value, path) {
    const record = expectRecord(value, path);
    exact(record, [
        "accountReady", "paymentMethodReady", "canManagePaymentMethod", "setupStatus", "checkoutAvailable", "card", "canRetrySetup", "updatedAt"
    ], path);
    const setupStatus = expectString(record.setupStatus, `${path}.setupStatus`);
    if (!STUDENT_BILLING_PAYMENT_SETUP_STATUSES.includes(setupStatus)) {
        throw new Error(`${path}.setupStatus is invalid`);
    }
    return {
        accountReady: expectBoolean(record.accountReady, `${path}.accountReady`),
        paymentMethodReady: expectBoolean(record.paymentMethodReady, `${path}.paymentMethodReady`),
        canManagePaymentMethod: expectBoolean(record.canManagePaymentMethod, `${path}.canManagePaymentMethod`),
        setupStatus: setupStatus,
        checkoutAvailable: expectBoolean(record.checkoutAvailable, `${path}.checkoutAvailable`),
        card: record.card === null ? null : parseSafePaymentCard(record.card),
        canRetrySetup: expectBoolean(record.canRetrySetup, `${path}.canRetrySetup`),
        updatedAt: nullableIsoString(record.updatedAt, `${path}.updatedAt`)
    };
}
function parseStudent(value, path) {
    const record = expectRecord(value, path);
    exact(record, ["studentId", "displayName", "collectionMethod"], path);
    const displayName = expectString(record.displayName, `${path}.displayName`).trim();
    if (!displayName)
        throw new Error(`${path}.displayName is required`);
    return {
        studentId: parseStudentBillingIdentifier(record.studentId, `${path}.studentId`),
        displayName,
        collectionMethod: (() => {
            const method = expectString(record.collectionMethod, `${path}.collectionMethod`);
            if (!STUDENT_BILLING_COLLECTION_METHODS.includes(method))
                throw new Error(`${path}.collectionMethod is invalid`);
            return method;
        })()
    };
}
function parseCharge(value, path) {
    const record = expectRecord(value, path);
    exact(record, [
        "executionId", "billingUnitId", "studentId", "studentDisplayName", "chargeType", "serviceMonth",
        "amount", "currency", "status", "scheduledChargeDate", "attemptedAt", "paidAt", "safeFailureCode",
        "canRetry", "updatedAt"
    ], path);
    const chargeType = expectString(record.chargeType, `${path}.chargeType`);
    if (!STUDENT_BILLING_CHARGE_TYPES.includes(chargeType)) {
        throw new Error(`${path}.chargeType is invalid`);
    }
    const status = expectString(record.status, `${path}.status`);
    if (!STUDENT_BILLING_PAYMENT_EXECUTION_STATUSES.includes(status)) {
        throw new Error(`${path}.status is invalid`);
    }
    const studentDisplayName = expectString(record.studentDisplayName, `${path}.studentDisplayName`).trim();
    if (!studentDisplayName)
        throw new Error(`${path}.studentDisplayName is required`);
    const safeFailureCode = nullableString(record.safeFailureCode, `${path}.safeFailureCode`);
    if (safeFailureCode !== null && !STUDENT_BILLING_SAFE_PAYMENT_FAILURE_CODES.includes(safeFailureCode)) {
        throw new Error(`${path}.safeFailureCode is invalid`);
    }
    if (record.currency !== "JPY")
        throw new Error(`${path}.currency must be JPY`);
    return {
        executionId: parseStudentBillingIdentifier(record.executionId, `${path}.executionId`),
        billingUnitId: parseStudentBillingIdentifier(record.billingUnitId, `${path}.billingUnitId`),
        studentId: parseStudentBillingIdentifier(record.studentId, `${path}.studentId`),
        studentDisplayName,
        chargeType: chargeType,
        serviceMonth: record.serviceMonth === null ? null : parseStudentBillingServiceMonth(record.serviceMonth, `${path}.serviceMonth`),
        amount: parseStudentBillingAmount(record.amount, `${path}.amount`),
        currency: "JPY",
        status: status,
        scheduledChargeDate: parseStudentBillingDate(record.scheduledChargeDate, `${path}.scheduledChargeDate`),
        attemptedAt: nullableIsoString(record.attemptedAt, `${path}.attemptedAt`),
        paidAt: nullableIsoString(record.paidAt, `${path}.paidAt`),
        safeFailureCode: safeFailureCode,
        canRetry: expectBoolean(record.canRetry, `${path}.canRetry`),
        updatedAt: isoString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseStudentBillingPayerOverviewDto(value, path = "studentBillingPayerOverview") {
    const record = expectRecord(value, path);
    const access = expectString(record.access, `${path}.access`);
    if (access === "notPayer") {
        exact(record, ["access"], path);
        return { access };
    }
    if (access !== "payer")
        throw new Error(`${path}.access is invalid`);
    exact(record, ["access", "paymentMethod", "assignedStudents", "charges", "customerInvoices"], path);
    return {
        access,
        paymentMethod: record.paymentMethod === null ? null : parsePaymentMethod(record.paymentMethod, `${path}.paymentMethod`),
        assignedStudents: expectArray(record.assignedStudents, parseStudent, `${path}.assignedStudents`),
        charges: expectArray(record.charges, parseCharge, `${path}.charges`),
        customerInvoices: record.customerInvoices === undefined ? [] : expectArray(record.customerInvoices, parseStudentBillingPayerCustomerInvoiceDto, `${path}.customerInvoices`)
    };
}
